/**
 * LegendService
 *
 * URL building and caching service for layer legends.
 * Handles three legend URL strategies:
 *   1. layer.legend_url - Direct legend URL from database
 *   2. layer.config.legendUrl - EM2 imported layers
 *   3. WMS GetLegendGraphic - Built from source_url for WMS layers
 */

import { Layer, WMSLayerConfig } from '../../../types/layer';

// Cache for legend URLs to avoid recalculation
const legendUrlCache = new Map<string, string | null>();

/**
 * Get the legend URL for a layer
 *
 * Priority:
 *   1. layer.legend_url (direct database field)
 *   2. layer.config.legendUrl (EM2 imports)
 *   3. Build GetLegendGraphic URL for WMS layers
 *
 * @param layer - The layer to get legend URL for
 * @returns Legend URL string or null if no legend available
 */
export function getLegendUrl(layer: Layer): string | null {
  // Check cache first
  const cacheKey = layer.id;
  if (legendUrlCache.has(cacheKey)) {
    return legendUrlCache.get(cacheKey) ?? null;
  }

  let legendUrl: string | null = null;

  // Priority 1: Direct legend_url from database
  if (layer.legend_url) {
    legendUrl = layer.legend_url;
  }
  // Priority 2: EM2 imported layers have legendUrl in config
  else if (layer.config?.legendUrl) {
    legendUrl = layer.config.legendUrl;
  }
  // Priority 3: Build GetLegendGraphic URL for WMS layers
  else if (layer.type === 'wms' && layer.source_url) {
    legendUrl = buildWmsLegendUrl(layer);
  }

  // Cache the result
  legendUrlCache.set(cacheKey, legendUrl);

  return legendUrl;
}

/**
 * Build a WMS GetLegendGraphic URL from layer configuration
 *
 * @param layer - WMS layer with source_url and config
 * @returns GetLegendGraphic URL or null if missing required fields
 */
function buildWmsLegendUrl(layer: Layer): string | null {
  if (!layer.source_url) {
    return null;
  }

  const config = layer.config as WMSLayerConfig | undefined;
  if (!config?.layers) {
    return null;
  }

  let parsed: URL | null = null;
  try {
    parsed = new URL(layer.source_url);
  } catch {
    parsed = null;
  }

  // External GeoServers (e.g. CWFIS/FireSTARR on Azure) own their own symbology
  // and serve a native GetLegendGraphic — and often require their own query
  // (e.g. ?authkey=). Use the ORIGIN server's legend directly, preserving that
  // query. Our own GeoServer stays behind the same-origin proxy (#621) so the
  // html2canvas Print/PDF export canvas is never tainted.
  if (parsed && !isOurGeoserverHost(parsed.hostname)) {
    // Keep the origin query (authkey, …) but drop unresolved ${...} templates
    // (e.g. a time param interpolated elsewhere) — the legend does not need them.
    const params = new URLSearchParams(parsed.searchParams);
    params.delete('');
    for (const [k, v] of [...params]) {
      if (/\$\{.*\}/.test(v)) params.delete(k);
    }
    params.set('SERVICE', 'WMS');
    params.set('REQUEST', 'GetLegendGraphic');
    params.set('VERSION', config.version || '1.3.0');
    params.set('FORMAT', 'image/png');
    params.set('LAYER', config.layers);
    params.set('TRANSPARENT', 'true');
    if (config.styles) params.set('STYLE', config.styles);
    return `${parsed.origin}${parsed.pathname}?${params.toString()}`;
  }

  // Our GeoServer (or a relative/unparseable URL): route through the proxy.
  let baseUrl: string;
  if (parsed) {
    baseUrl = `/api/geoserver${parsed.pathname}`;
  } else {
    const path = layer.source_url.split('?')[0];
    baseUrl = path.startsWith('/api/geoserver') ? path : `/api/geoserver${path.startsWith('/') ? '' : '/'}${path}`;
  }

  const params = new URLSearchParams({
    SERVICE: 'WMS',
    REQUEST: 'GetLegendGraphic',
    VERSION: config.version || '1.3.0',
    FORMAT: 'image/png',
    LAYER: config.layers,
    TRANSPARENT: 'true',
  });
  if (config.styles) {
    params.set('STYLE', config.styles);
  }
  // Layer-group legends decompose into one titled section per member layer
  // (the member's name). forceTitles:off renders a single combined legend
  // without those per-member headers. Harmless for single-layer legends, which
  // carry no title anyway. Only applied to our own GeoServer (proxied) legends.
  params.set('LEGEND_OPTIONS', 'forceTitles:off');

  return `${baseUrl}?${params.toString()}`;
}

/** True for our own GeoServer hosts (gs<year>.intellifirenwt.com) that need the proxy. */
function isOurGeoserverHost(hostname: string): boolean {
  return /(^|\.)intellifirenwt\.com$/i.test(hostname);
}

/**
 * Programmatic legend item — color swatch + label
 */
export interface ProgrammaticLegendItem {
  color: string;
  label: string;
}

/**
 * Get programmatic legend items from a layer's config
 *
 * @param layer - The layer to check
 * @returns Array of legend items or null if none defined
 */
export function getProgrammaticLegendItems(layer: Layer): ProgrammaticLegendItem[] | null {
  const items = layer.config?.legendItems;
  if (Array.isArray(items) && items.length > 0) {
    return items as ProgrammaticLegendItem[];
  }
  return null;
}

/**
 * Check if a layer has legend capability
 *
 * @param layer - The layer to check
 * @returns true if the layer can display a legend
 */
export function hasLegendCapability(layer: Layer): boolean {
  // Has programmatic legend items in config
  if (getProgrammaticLegendItems(layer)) return true;

  // Has direct legend URL
  if (layer.legend_url) return true;

  // Has EM2 legendUrl in config
  if (layer.config?.legendUrl) return true;

  // Is WMS layer with required fields for GetLegendGraphic
  if (layer.type === 'wms' && layer.source_url && layer.config?.layers) {
    return true;
  }

  return false;
}

/**
 * Clear the legend URL cache
 * Call this if layer configurations change
 */
export function clearLegendCache(): void {
  legendUrlCache.clear();
}

/**
 * Remove a specific layer from the cache
 *
 * @param layerId - The layer ID to remove from cache
 */
export function invalidateLegendCache(layerId: string): void {
  legendUrlCache.delete(layerId);
}
